import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import Checkbox from '@mui/material/Checkbox';
import PropTypes from 'prop-types';
import ChevronUp from '../Svg/ChevronUp/ChevronUp';
import ChevronDown from '../Svg/ChevronDown/ChevronDown';
import Typography from '../Typography';
import track from '../../analytics/track';
import urls from '../../utils/urls';

const Filters = ({ data = [], loading, locale, selectedFilters = [], setLoading }) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedFiltersState, setSelectedFiltersState] = useState([]);
  const filtersWrapper = useRef(null);
  const activeTimer = useRef(null);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // translations
  const t = useTranslations('Filters');

  // click handler for the filters "Show me" button
  const handleFiltersClick = () => {
    setFiltersOpen(!filtersOpen);
  };

  // click handler for filters
  const filterClick = (id, title) => {
    setLoading(true);

    // update state with the checked checkboxes first
    // so we can show it on screen without having to wait for results to update
    let newState = [];
    if (!selectedFiltersState.includes(id)) {
      newState = [...selectedFiltersState, id];

      // analytics
      track.event({
        eventInfo: {
          category: 'category filter',
          action: 'clicked',
          details: `${title}, checked`, // Cake, Cupcake, Custom, etc. referencing filter.title values // checked //
          value: 1,
        },
        template: pathname.includes(urls.search) ? 'search' : 'subcategory',
        language: locale,
      });
    } else {
      newState = selectedFiltersState?.filter((v) => { return v !== id; });
    }
    setSelectedFiltersState(newState);
  };

  // click handler for the Clear filters button
  const handleClearClick = () => {
    setLoading(true);
    setSelectedFiltersState([]);
  };

  // close icon for the CLEAR button in filters menu
  const filtersCloseButton = (
    <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 16" width="14" height="14">
      <g clipPath="url(#88ar8wu0da)">
        <path d="m8.5 6.99 2.828-2.828.943.942-2.828 2.829 2.828 2.828-.943.943L8.5 8.876l-2.828 2.828-.943-.943 2.828-2.828L4.73 5.104l.943-.942L8.5 6.99z" fill="currentColor" />
      </g>
      <defs>
        <clipPath id="88ar8wu0da">
          <path fill="#fff" d="M.5 0h16v16H.5z" />
        </clipPath>
      </defs>
    </svg>
  );

  // close filters menu if you click outside the menu
  useEffect(() => {
    const determineClick = (e) => {
      e.stopPropagation();

      // determine if click is inside the wrapper
      const filtersEl = filtersWrapper?.current?.contains(e.target);

      // if the click is outside the wrapper, close filters menu
      if (filtersOpen && !filtersEl) {
        setFiltersOpen(false);
      }
    };

    document.addEventListener('mousedown', determineClick);

    return () => {
      document.removeEventListener('mousedown', determineClick);
    };
  }, [filtersOpen]);

  // icons
  const CheckboxIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
      <path stroke="#78757E" d="M4 .5h16A3.5 3.5 0 0 1 23.5 4v16a3.5 3.5 0 0 1-3.5 3.5H4A3.5 3.5 0 0 1 .5 20V4A3.5 3.5 0 0 1 4 .5Z" />
    </svg>
  );
  const CheckboxCheckedIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
      <path fill="#333040" d="M0 4a4 4 0 0 1 4-4h16a4 4 0 0 1 4 4v16a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4Z" />
      <path fill="#fff" d="m5 12 5.25 5L19 8.666 17.25 7l-7 6.667-3.5-3.334L5 11.999Z" />
    </svg>
  );

  // if filters change, set a timer before redirecting
  useEffect(() => {
    // using the filters in state (selectedFiltersState), populte the "filters"
    // query string param and redirect to the current URL with it
    const redirectWithNewFilters = () => {
      // redirect with the new filters query string value
      const newQuery = new URLSearchParams(searchParams.toString());
      if (newQuery.has('page') && newQuery.get('page') !== 'all') {
        newQuery.delete('page');
      }
      newQuery.set('filters', selectedFiltersState.join(','));
      router.push(`${pathname}?${newQuery.toString()}`);
    };

    if (loading) {
      // clear active timer if there is one
      if (activeTimer.current) {
        clearTimeout(activeTimer.current);
        activeTimer.current = null;
      }

      // we wait 400ms to give the user time to check other filters before filtering
      const timeoutId = setTimeout(() => {
        redirectWithNewFilters();
        clearTimeout(timeoutId);
        activeTimer.current = null;
      }, 400);

      // store the active timer
      activeTimer.current = timeoutId;
    }

    return () => { clearTimeout(activeTimer.current); };
  }, [selectedFiltersState, loading, searchParams, router, pathname]);

  // populate filters in state with the filters from the searchParams
  useEffect(() => {
    setSelectedFiltersState(selectedFilters);
    setLoading(false);
  }, [selectedFilters, setLoading]);

  return (
    <div
      className={classNames('filters-container', { open: filtersOpen })}
      ref={filtersWrapper}
    >
      <button
        // aria-label={`Show me ${selectedFiltersState?.length}`}
        aria-label={t('ctas.showMe.ariaLabel', { length: selectedFiltersState?.length })}
        type="button"
        className="filters-button"
        onClick={handleFiltersClick}
        aria-expanded={filtersOpen}
      >
        <span className="filters-label-wrapper">
          <Typography variant="body-medium">{t('ctas.showMe.label')}</Typography>
          {!!selectedFiltersState?.length && (
          <div className="filters-count">
            <Typography variant="eyebrow-medium">
              {selectedFiltersState?.length}
            </Typography>
          </div>
          )}
        </span>

        {filtersOpen ? <ChevronUp /> : <ChevronDown />}
      </button>

      <div className="filters-menu" style={{ display: filtersOpen ? null : 'none' }}>
        {data.map((filter) => {
          return (
            <button
              className="filter-item"
              type="button"
              onClick={() => { filterClick(filter.key, filter.title); }}
              disabled={!filter.count}
              key={filter.key}
              aria-label={`${filter.title}, ${filter.count} ${selectedFiltersState.includes(filter.key) ? 'checked' : 'unchecked'}`}
            >
              <Checkbox
                className="filter-checkbox"
                size="medium"
                checked={selectedFiltersState.includes(filter.key)}
                disabled={!filter.count}
                checkedIcon={CheckboxCheckedIcon}
                icon={CheckboxIcon}
                inputProps={{
                  'aria-label': filter.title,
                  'aria-checked': selectedFiltersState.includes(filter.key),
                }}
                tabIndex="-1"
              />
              <Typography variant="body" addClasses="filter-label" ariaLabel={`${filter.title} checked ${selectedFilters.includes(filter.id)}`}>{filter.title}</Typography>
              <Typography variant="body" addClasses="filter-count">{`(${filter.count})`}</Typography>
            </button>
          );
        })}

        <div className="filters-clear">
          <button
            type="button"
            onClick={handleClearClick}
            disabled={!selectedFilters?.length}
          >
            {filtersCloseButton}
            <Typography variant="eyebrow">{t('ctas.clear.label')}</Typography>
          </button>
        </div>
      </div>
    </div>
  );
};

Filters.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  selectedFilters: PropTypes.array,
  setLoading: PropTypes.func.isRequired,
  locale: PropTypes.string,
};

export default Filters;
